import SettingsContainer from "../../components/Layout/SettingsContainer";
import LabelContainer from "../../components/SettingsComponent/labelContainer";
import ComponentThemeType from "../../models/ComponentTheme";
import ComponentTypeEnum from "../../models/ComponentTypeEnum";
import React, { useEffect, useState } from "react";
import { spacing_l } from "../../StyleHelpers";
import H4 from "../../components/Typography/H4";
import { Dropdown } from "react-native-element-dropdown";
import localStorage from "@react-native-async-storage/async-storage";
import { AsyncStorageKeys, CarActivity } from "../../Types";
import ButtonToggle from "../../components/ButtonToggle";
import iconChevron from "../../assets/Icons/icon_chevron-down.svg";
import Image from "../../components/Image/Image";
import Toast from "../../components/Toast";
import { checkApiKey } from "../../services/apiKeyService";
import { removeCacheItem } from "../../utils/CacheStorageUtils";
import useGenericContentStore from "../../store/genericContent.store";
import ENVIRONMENT from "../../config/ENVIRONMENT";
import { DEFAULT_ENV } from "../../config/ENV_CONFIG";
import useDrivingStore from "../../store/useDriving.store";
import languagesList from "../../assets/Language/languages.json";
import localeByCountry from "../../assets/Language/localeByCountry.json";
import { globalAny } from "../../utils/Utils";
import { AppLogger } from "../../utils/AppLogger";
import useLegalContentStore from "../../store/useLegalContent.store";
import {
	Label,
	PaddedContainer,
	StyledAdvance,
	StyledButton,
	StyledCenteredContainer,
	StyledContainerAdvance,
	StyledMarginTop,
	StyledPartialSettingsContainer,
	stylesAdvance,
} from "./styled";
import { View } from "react-native";

const SettingsAdvance = () => {
	const { carActivity, setCarActivity } = useDrivingStore((state: any) => ({
		carActivity: state.carActivity,
		setCarActivity: state.setCarActivity,
	}));

	const { isEnabledFloatingButton, setIsEnabledFloatingButton } = useGenericContentStore((state: any) => ({
		isEnabledFloatingButton: state.isEnabledFloatingButton,
		setIsEnabledFloatingButton: state.setIsEnabledFloatingButton,
	}));

	const { fetchLegals, fetchAcceptOtherData } = useLegalContentStore((state: any) => ({
		fetchLegals: state.fetchLegals,
		fetchAcceptOtherData: state.fetchAcceptOtherData,
	}));

	// Local state
	const [selectedRegion, setSelectedRegion] = useState("Unset");
	const [selectedLanguage, setSelectedLanguage] = useState("Unset");
	const [selectedEnvironment, setSelectedEnvironment] = useState("Unset");
	const [selectedCarActivity, setSelectedCarActivity] = useState<any>(carActivity);

	const [disableMqtt, setDisableMqtt] = useState(true);
	const [disableReload, setDisableReload] = useState(false);
	const [enableFloatingButton, setEnableFloatingButton] = useState<any>(isEnabledFloatingButton);

	const [visible, setVisible] = useState(false);
	const [toastMessage, setToastMessage] = useState("");
	const [labelText, setLabelText] = useState("");

	const [countryData, setCountryData] = useState<any>([]);
	const [languages, setLanguages] = useState<any>([]);

	// Handlers
	const onDismissSnackBar = () => setVisible(false);

	/**
	 * Processes the localeByCountry list to create an array of country objects
	 * formatted with language code, country name (with uppercase country code),
	 * and country code, then updates the countryData state.
	 */
	const getCountries = () => {
		const data = localeByCountry.map((item: any) => {
			let languageCode = item.locales.toUpperCase();
			let countryName = `${item.en_name} - ${item.country.toUpperCase()}`;
			let countryCode = item.country.toUpperCase();
			return {
				id: languageCode,
				label: countryName,
				value: countryCode,
			};
		});

		setCountryData(data);
	};

	/**
	 * Processes the languagesList to create an array of language objects
	 * formatted with language code as id and value, and language name as label,
	 * then updates the languages state.
	 */
	const getLanguages = () => {
		const data = languagesList.map((item: any) => {
			let languageCode = item.code;
			return {
				id: languageCode,
				label: item.language,
				value: languageCode,
			};
		});

		setLanguages(data);
	};

	/**
	 * Updates the languages state based on the provided countryCode.
	 * If the countryCode contains multiple codes separated by commas,
	 * it splits them and filters languages for each code,
	 * otherwise filters languages for the single countryCode.
	 *
	 * @param {string} countryCode - A single or comma-separated string of country codes.
	 */
	const getLocaleByCountry = (countryCode: string) => {
		if (countryCode.includes(",")) {
			const multipleCountryCode = countryCode.split(",");
			const multipleLanguages = multipleCountryCode.map((code) => filteredLanguages(code));
			setLanguages(multipleLanguages.filter((lang) => lang));
			return;
		}

		setLanguages([filteredLanguages(countryCode)]);
	};

	/**
	 * Filters the languagesList by the exact country code,
	 * then returns a language object containing id, label, and value.
	 *
	 * @param {string} countryCode - The country code to filter by.
	 * @returns {object} - The language object matching the country code.
	 */
	const filteredLanguages = (countryCode: string) => {
		const [locale] = languagesList.filter((val) => val.code.trim() === countryCode.trim());

		if (!locale) return;

		return {
			id: locale.code,
			label: locale.language,
			value: locale.code,
		};
	};

	/**
	 * Initializes region, language, and environment selections from localStorage,
	 * then applies MQTT configuration settings.
	 */
	const initializeCountries = async () => {
		const regionCode: any = await localStorage.getItem(AsyncStorageKeys.RegionOverride);
		setSelectedRegion(regionCode);
		const langCode: any = await localStorage.getItem(AsyncStorageKeys.LanguageOverride);
		setSelectedLanguage(langCode);

		localStorage.getItem(AsyncStorageKeys.EnvironmentOverride).then((value) => {
			setSelectedEnvironment(value ?? "Unset");
		});

		configureMqttSettings();
	};

	/**
	 * Reads the MQTT disable flag from sessionStorage and updates the state accordingly.
	 */
	const configureMqttSettings = async () => {
		const disableMqttStore = sessionStorage.getItem("disableMqtt");

		setDisableMqtt(disableMqttStore === "true");
	};

	/**
	 * Sets the selected language override.
	 * @param {string} lang - Language code to set.
	 */
	const setLanguageOverride = (lang: string) => {
		setSelectedLanguage(lang);
	};

	/**
	 * Sets the selected region override and updates languages accordingly.
	 * @param {string} region - Region name or code to set.
	 * @param {string} id - Country code used to update locale languages.
	 */
	const setRegionOverride = (region: string, id: string) => {
		getLocaleByCountry(id);
		setSelectedRegion(region);
	};

	/**
	 * Sets the selected environment override and saves it in localStorage.
	 * @param {string} env - Environment string to set.
	 */
	function setEnvironmentOverride(env: string) {
		setSelectedEnvironment(env);
		localStorage.setItem(AsyncStorageKeys.EnvironmentOverride, env);
	}
	/**
	 * Resets the selected language override to "Unset".
	 */
	function unsetLanguageOverride() {
		setSelectedLanguage("Unset");
	}

	/**
	 * Resets the selected region override to "Unset".
	 */
	function unsetRegionOverride() {
		setSelectedRegion("Unset");
	}

	/**
	 * Resets the selected environment override to "Unset" and removes from localStorage.
	 */
	function unsetEnvironmentOverride() {
		setSelectedEnvironment("Unset");
		localStorage.removeItem(AsyncStorageKeys.EnvironmentOverride);
	}

	/**
	 * Enables or disables MQTT override and saves the setting in sessionStorage.
	 * @param {boolean} disable - Whether to disable MQTT.
	 */
	function setDisableMqttOverride(disable: boolean) {
		// @ts-ignore
		sessionStorage.setItem("disableMqtt", disable);
		setDisableMqtt(disable);
	}

	/**
	 * Reloads app settings after verifying the API key for the selected region.
	 * Clears cached items, sets language/region overrides in localStorage,
	 * updates document language attribute, fetches legal and acceptance data,
	 * and reloads the landing page.
	 * Shows a toast if the territory or language is not supported.
	 */
	const onReload = async () => {
		localStorage.setItem(AsyncStorageKeys.isOverride, "true");
		const result: any = await checkApiKey(selectedRegion);

		if (result?.apiKey && result?.responseCode) {
			removeCacheItem(AsyncStorageKeys.languageJson);
			removeCacheItem(AsyncStorageKeys.session);
			removeCacheItem(AsyncStorageKeys.sessionExpired);
			removeCacheItem(AsyncStorageKeys.email);
			removeCacheItem(AsyncStorageKeys.idleExpiry);
			removeCacheItem(AsyncStorageKeys.refreshToken);
			removeCacheItem(AsyncStorageKeys.subscription);
			removeCacheItem(AsyncStorageKeys.svodCountryId);
			localStorage.setItem(AsyncStorageKeys.LanguageOverride, selectedLanguage);
			localStorage.setItem(AsyncStorageKeys.RegionOverride, selectedRegion);
			document.documentElement.setAttribute("lang", selectedLanguage);

			try {
				await fetchLegals(AsyncStorageKeys.legals);
				await fetchAcceptOtherData(AsyncStorageKeys.acceptOtherData);
			} catch (err) {
				AppLogger.log(err);
			}
			window.location.replace("/landing");

			return;
		}

		removeCacheItem(AsyncStorageKeys.isOverride);
		setToastMessage(globalAny.language.territory_lang_not_supported);
		setLabelText("");
		setVisible(true);
	};

	/**
	 * Disable reload button if selected language or region is "Unset".
	 */
	useEffect(() => {
		if (selectedLanguage === "Unset" || selectedRegion === "Unset") {
			return setDisableReload(true);
		}
		setDisableReload(false);
	}, [selectedRegion, selectedLanguage]);

	/**
	 * Initialize car activity and floating button state on component mount.
	 */
	useEffect(() => {
		const initCarActivity = async () => {
			setSelectedCarActivity(carActivity);
			setEnableFloatingButton(isEnabledFloatingButton);
		};
		initCarActivity();
	}, []);

	/**
	 * Persist selectedCarActivity to localStorage and update carActivity state when it changes.
	 */
	useEffect(() => {
		localStorage.setItem(AsyncStorageKeys.carActivity, selectedCarActivity);
		setCarActivity(selectedCarActivity);
	}, [selectedCarActivity]);

	/**
	 * Persist carActivity to localStorage when it changes.
	 */
	useEffect(() => {
		localStorage.setItem(AsyncStorageKeys.carActivity, carActivity);
	}, [carActivity]);

	/**
	 * Persist floating button enabled state to localStorage and update internal state when it changes.
	 */
	useEffect(() => {
		localStorage.setItem(AsyncStorageKeys.floatingButton, enableFloatingButton);
		setIsEnabledFloatingButton(enableFloatingButton);
	}, [enableFloatingButton]);

	/**
	 * Fetch and set countries and languages data on component mount.
	 */
	useEffect(() => {
		getCountries();
		getLanguages();
	}, []);

	/**
	 * Initialize selected region, language, environment, and MQTT settings on component mount.
	 */
	useEffect(() => {
		initializeCountries();
	}, []);

	return (
		// @ts-ignore
		<StyledAdvance>
			{/* @ts-ignore */}
			<StyledMarginTop>
				<SettingsContainer>
					<LabelContainer body={"Clicking UNSET will revert to the default/detected setting."} />
					{/* @ts-ignore */}
					{DEFAULT_ENV !== ENVIRONMENT.PRODUCTION ? (
						/* @ts-ignore */
						<StyledPartialSettingsContainer>
							<LabelContainer header={"Environment"} />
							{/* @ts-ignore */}
							<StyledContainerAdvance>
								<StyledButton
									componentTheme={ComponentThemeType.VinFast}
									type={selectedEnvironment == "Unset" ? ComponentTypeEnum.Primary : ComponentTypeEnum.Secondary}
									onPress={unsetEnvironmentOverride}
								>
									UNSET
								</StyledButton>
								<StyledButton
									componentTheme={ComponentThemeType.VinFast}
									type={selectedEnvironment == "STAGING" ? ComponentTypeEnum.Primary : ComponentTypeEnum.Secondary}
									onPress={() => setEnvironmentOverride("STAGING")}
								>
									STAGING
								</StyledButton>
								<StyledButton
									componentTheme={ComponentThemeType.VinFast}
									type={selectedEnvironment == "UAT" ? ComponentTypeEnum.Primary : ComponentTypeEnum.Secondary}
									onPress={() => setEnvironmentOverride("UAT")}
								>
									UAT
								</StyledButton>
								<StyledButton
									componentTheme={ComponentThemeType.VinFast}
									type={selectedEnvironment == "PRODUCTION" ? ComponentTypeEnum.Primary : ComponentTypeEnum.Secondary}
									onPress={() => setEnvironmentOverride("PRODUCTION")}
								>
									PRODUCTION
								</StyledButton>
							</StyledContainerAdvance>
						</StyledPartialSettingsContainer>
					) : (
						<></>
					)}
					{/* @ts-ignore */}
					<StyledPartialSettingsContainer>
						<LabelContainer header={"Car Activity"} />

						{/* @ts-ignore */}
						<PaddedContainer>
							<StyledButton
								componentTheme={ComponentThemeType.VinFast}
								type={carActivity === CarActivity.INMOTION ? ComponentTypeEnum.Primary : ComponentTypeEnum.Secondary}
								onPress={() => setSelectedCarActivity(CarActivity.INMOTION)}
							>
								{" "}
								IN-MOTION{" "}
							</StyledButton>
							<StyledButton
								componentTheme={ComponentThemeType.VinFast}
								type={carActivity === CarActivity.PARKED ? ComponentTypeEnum.Primary : ComponentTypeEnum.Secondary}
								onPress={() => setSelectedCarActivity(CarActivity.PARKED)}
								style={{
									marginLeft: spacing_l,
								}}
							>
								{" "}
								PARKED{" "}
							</StyledButton>
						</PaddedContainer>
					</StyledPartialSettingsContainer>
					{/* @ts-ignore */}
					<StyledPartialSettingsContainer>
						<View
							style={{
								alignItems: "center",
								flexBasis: "100%",
								width: "100%",
								flexDirection: "row",
								justifyContent: "flex-start",
								paddingVertical: 15,
							}}
						>
							<ButtonToggle defaultValue={enableFloatingButton} onPress={(state) => setEnableFloatingButton(state)} />
							<View style={{ marginLeft: "2%" }}>
								<H4>Show this as a floating button</H4>
							</View>
						</View>
					</StyledPartialSettingsContainer>
					{/* @ts-ignore */}
					<StyledPartialSettingsContainer>
						<Label header={"Territory"} />

						{/* @ts-ignore */}
						<PaddedContainer>
							<StyledButton
								componentTheme={ComponentThemeType.VinFast}
								type={selectedRegion == "Unset" ? ComponentTypeEnum.Primary : ComponentTypeEnum.Secondary}
								onPress={unsetRegionOverride}
							>
								{" "}
								UNSET{" "}
							</StyledButton>

							<Dropdown
								style={[stylesAdvance.dropdown]}
								placeholderStyle={stylesAdvance.placeholderStyle}
								selectedTextStyle={stylesAdvance.selectedTextStyle}
								inputSearchStyle={stylesAdvance.inputSearchStyle}
								searchPlaceholder="Search..."
								data={countryData}
								labelField={"label"}
								valueField={"value"}
								dropdownPosition={"bottom"}
								search
								onChange={(item) => setRegionOverride(item.value, item.id)}
								value={selectedRegion}
								renderRightIcon={() => (
									<Image
										source={{
											uri: iconChevron,
										}}
										resizeMode="stretch"
										width={24}
										height={24}
									/>
								)}
							/>
						</PaddedContainer>
					</StyledPartialSettingsContainer>
					{/* @ts-ignore */}
					<StyledPartialSettingsContainer>
						<LabelContainer header={"Language"} />

						{/* @ts-ignore */}
						<PaddedContainer>
							<StyledButton
								componentTheme={ComponentThemeType.VinFast}
								type={selectedLanguage == "Unset" ? ComponentTypeEnum.Primary : ComponentTypeEnum.Secondary}
								onPress={unsetLanguageOverride}
							>
								{" "}
								UNSET{" "}
							</StyledButton>

							<Dropdown
								style={[stylesAdvance.dropdown]}
								placeholderStyle={stylesAdvance.placeholderStyle}
								selectedTextStyle={stylesAdvance.selectedTextStyle}
								inputSearchStyle={stylesAdvance.inputSearchStyle}
								searchPlaceholder="Search..."
								data={languages}
								labelField={"label"}
								valueField={"value"}
								dropdownPosition={"bottom"}
								search
								onChange={(item) => setLanguageOverride(item.id)}
								value={selectedLanguage}
								renderRightIcon={() => (
									<Image
										source={{
											uri: iconChevron,
										}}
										resizeMode="stretch"
										width={24}
										height={24}
									/>
								)}
							/>
						</PaddedContainer>
					</StyledPartialSettingsContainer>
					{/* @ts-ignore */}
					{DEFAULT_ENV !== ENVIRONMENT.PRODUCTION ? (
						//@ts-ignore
						<StyledPartialSettingsContainer>
							<LabelContainer header={"Theme"} />
							{/* @ts-ignore */}
							<PaddedContainer>
								<StyledButton componentTheme={ComponentThemeType.VinFast} type={ComponentTypeEnum.Primary}>
									DARK
								</StyledButton>
								<StyledButton
									componentTheme={ComponentThemeType.VinFast}
									type={ComponentTypeEnum.Secondary}
									disabled={true}
									style={{
										marginLeft: spacing_l,
									}}
								>
									LIGHT
								</StyledButton>
							</PaddedContainer>
						</StyledPartialSettingsContainer>
					) : (
						<></>
					)}
					{/* @ts-ignore */}
					{DEFAULT_ENV !== ENVIRONMENT.PRODUCTION ? (
						//@ts-ignore
						<StyledPartialSettingsContainer>
							<View
								style={{
									alignItems: "center",
									flexBasis: "100%",
									width: "100%",
									flexDirection: "row",
									justifyContent: "flex-start",
									paddingVertical: 15,
								}}
							>
								<ButtonToggle defaultValue={!disableMqtt} onPress={(state) => setDisableMqttOverride(!state)} />
								<View style={{ marginLeft: "2%" }}>
									<H4>MQTT Functionality</H4>
								</View>
							</View>
						</StyledPartialSettingsContainer>
					) : (
						<></>
					)}
				</SettingsContainer>
			</StyledMarginTop>
			{/* @ts-ignore */}
			<StyledCenteredContainer>
				<StyledButton
					componentTheme={ComponentThemeType.VinFast}
					disabled={disableReload}
					type={ComponentTypeEnum.Secondary}
					onPress={onReload}
				>
					{`Reload`}
				</StyledButton>
			</StyledCenteredContainer>
			<Toast visible={visible} text={toastMessage} label={labelText} onDismissSnackBar={onDismissSnackBar}></Toast>
		</StyledAdvance>
	);
};

export default SettingsAdvance;
